import { createInertiaApp } from "@inertiajs/react"
import { type ReactNode, createElement } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import MarketingLayout from "@/layouts/marketing-layout"
import { STANDALONE_PAGES } from "@/layouts/standalone-pages"
import PersistentLayout from "@/layouts/persistent-layout"

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

const appName = (import.meta.env.VITE_APP_NAME ?? "Inco") as string

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => `${title} - ${appName}`,

// Resolved lazily, one chunk per page (INCO-401).
//
// `eager: true` pulled every page component into a single chunk: 2,631 kB
// raw, and Lighthouse reported 1,927 KiB of it unused on the landing page.
// A marketing page needs its own template, the marketing shell and React —
// not the deal blotter, the assistant panel and the chart library. Vite's
// own build output had been printing the "consider dynamic import()"
// warning about this on every build.
//
// Inertia awaits `resolve` before it renders or hydrates, so the extra round
// trip lands before first paint rather than as a flash of nothing. The SSR
// entrypoint keeps its eager glob: there is no network in a Node process,
// and lazy chunks there would buy nothing.
resolve: async (name) => {
  // Everything but the staff consoles, which are mapped in `staff-pages.ts`
  // and fetched only when the server names one (INCO-518): a glob is an
  // object literal keyed by path, and this chunk is the one everybody gets.
  const pages = import.meta.glob<ResolvedComponent>([
    "../pages/**/*.tsx",
    "!../pages/staff/**",
  ])
  let importPage = pages[`../pages/${name}.tsx`]
  if (!importPage && name.startsWith("staff/")) {
    const { staffPages } = await import("../staff-pages")
    importPage = staffPages[`./pages/${name}.tsx`]
  }
  if (!importPage) {
    throw new Error(`Missing Inertia page component: '${name}.tsx'`)
  }

  const page = await importPage()

  // Layout by resolution, not by hand (INCO-395).
  //
  // `??=` so a page that assigns its own layout keeps it — the desk profile
  // does, because it is on the marketing shell without living under
  // `pages/marketing/`.
  //
  // Everything under `pages/marketing/` gets the marketing shell. Before
  // this, each page assigned it at the bottom of the file, and a page that
  // forgot fell through to `PersistentLayout` — which mounts the assistant
  // provider, the ⌘K palette and the shortcuts dialog for an anonymous
  // visitor who can reach none of them. The failure was silent and looked
  // fine.
  page.default.layout ??= STANDALONE_PAGES.has(name)
    ? (page) => page
    : name.startsWith("marketing/")
      ? (page) => createElement(MarketingLayout, null, page)
      : (page) => createElement(PersistentLayout, null, page)

  return page
},

  setup({ el, App, props }) {
    // Hydrate what the server already rendered, rather than throwing it away
    // and rendering again (INCO-399). Only the marketing pages arrive with
    // children — SSR is enabled per controller — so everything else takes the
    // `createRoot` path exactly as before.
    if (el.hasChildNodes()) {
      hydrateRoot(el, createElement(App, props))
      return
    }
    createRoot(el).render(createElement(App, props))
  },

  progress: {
    // The page-load bar is the one place the accent is allowed to be a
    // full-width element, and it should be oxblood like every other primary.
    color: "var(--primary)",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
